import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { PaymentReceipt } from '../types/user.types';

const STALE_RECEIPT_THRESHOLD = 600000;

interface PaymentHandlerState {
  status: 'IDLE' | 'PROCESSING' | null;
  paymentId: number | null;
}

export function usePaymentReceiptHandler() {
  const [state, setState] = useState<PaymentHandlerState>({ status: null, paymentId: null });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get('status');

    if (paymentStatus?.toUpperCase() === 'SUCCESS') {
      const paymentIdString = urlParams.get('paymentId');
      const paymentId = paymentIdString ? parseInt(paymentIdString, 10) : NaN;

      if (isNaN(paymentId)) {
        console.error('Novalnet redirect successful, but paymentId is missing or not a number.');
        setState({ status: 'IDLE', paymentId: null });

        return;
      }

      const paymentReceipt: PaymentReceipt = {
        status: 'PROCESSING',
        timestamp: Date.now(),
        paymentId: paymentId,
      };
      sessionStorage.setItem('paymentReceipt', JSON.stringify(paymentReceipt));
      navigate({ pathname: location.pathname, search: '', hash: location.hash }, { replace: true });
      setState({ status: 'PROCESSING', paymentId: paymentId });

      return;
    }

    try {
      const receiptString = sessionStorage.getItem('paymentReceipt');
      if (receiptString) {
        const receipt: PaymentReceipt = JSON.parse(receiptString);
        if (Date.now() - receipt.timestamp < STALE_RECEIPT_THRESHOLD) {
          setState({ status: 'PROCESSING', paymentId: receipt.paymentId });
        } else {
          sessionStorage.removeItem('paymentReceipt');
          setState({ status: 'IDLE', paymentId: null });
        }
      } else {
        setState({ status: 'IDLE', paymentId: null });
      }
    } catch (error) {
      console.error('Failed to parse payment receipt.', error);
      sessionStorage.removeItem('paymentReceipt');
      setState({ status: 'IDLE', paymentId: null });
    }
  }, [location.search]);

  const clearStatus = useCallback(() => {
    sessionStorage.removeItem('paymentReceipt');
    setState((prevState) => ({ ...prevState, status: 'IDLE' }));
  }, []);

  return { ...state, clearStatus };
}
